<template lang="pug">
v-container(style="max-width:960px")
  .text-center(v-if="isLoading")
    v-progress-circular(width="2" size="50" color="primary" indeterminate)
  template(v-else)
    template(v-if="segment")
      v-row.mt-sm-8(no-gutters)
        v-col(cols="12" md="6")
          .d-flex.align-center
            template(v-if="isEditingTitle")
              v-form(ref="form" @submit.prevent)
                yc-text-field.mr-2(label="Segment name" v-model="editedName" :rules="rules.not_empty" validate-on-blur)
              v-btn.mr-2.button-Aa-medium(@click="saveEdit" :loading="isSaving" color="info" height=40 depressed) {{ $t('save') }}  
              v-btn.button-Aa-medium(@click="isEditingTitle = false" color="primary400" height=40 text) {{ $t('cancel') }}
            template(v-else)
              h5.mr-2 {{ segment.title }}
              v-btn(@click="editSegmentName" color="primary400" icon)
                v-icon(small) $yzi-edit-02
          div
            template(v-if="isEditingDescription")
              yc-text-field.mr-2.mb-2(:label="$t('description')" v-model="editedDescription")
              v-btn.mr-2.button-Aa-medium(@click="saveEditDescription" :loading="isSavingDescription" color="info" height=40 depressed) {{ $t('save') }} 
              v-btn.button-Aa-medium(@click="isEditingDescription = false" color="primary400" height=40 text) {{ $t('cancel') }}
            template(v-else)
              .text-sm.font-normal.primary400--text {{ segment.description || 'No description' }}
                span
                  v-btn(@click="editSegmentDescription" color="primary400" icon)
                    v-icon(small) $yzi-edit-02
        v-col.d-flex(cols="12" md="6")
          v-spacer
          v-btn.px-4.mr-4.button-Aa-medium.round-lg(color="primary" height="40" outlined @click="blastSmsToAll")
            v-icon(left color="primary") $yzi-message-circle-01
            | {{ $t('blast_sms') }}
          v-btn.px-4.mr-4.button-Aa-medium.round-lg(color="primary" height="40" outlined @click="blastEmailToAll")
            v-icon(left color="primary") $yzi-mail-01
            | {{ $t('blast_email') }}
          v-menu(offset-y)
            template(v-slot:activator="{ on, attrs }")
              v-btn.pl-1.pr-4.button-Aa-medium.round-lg(v-bind="attrs" v-on="on" depressed color="info500" height="40" dark) 
                eva-icon.mr-1.mt-1(name="chevron-down" fill="white" width="26" height="26")
                span {{ $t('add_people') }}
            v-list
              v-list-item(v-for="(item, index) in addOptions" :key="index" @click="openAddPage(item)")
                .p2 {{ item.title }}
          v-menu(offset-y)
            template(v-slot:activator="{ on, attrs }")
              v-btn.ml-2(v-on="on" color="black" icon)
                v-icon mdi-dots-horizontal
            v-list
              v-list-item(@click="showDeleteDialog = true")
                .p2 {{ $t('delete_segment') }}

      div.main-container.my-5
        v-row(no-gutters)
          v-col(cols="12" sm="6")
            v-tabs(v-model="campaignTab" color="basic700" :height="40" grow)
              v-tab.text-md.font-medium.text-capitalize {{ $t('patient') }}
              v-tab.text-md.font-medium.text-capitalize {{ $t('sms_blast_report') }}
              v-tab.text-md.font-medium.text-capitalize {{ $t('email_blast_report') }}
        div.separator(:style="{ 'background-color':$vuetify.theme.themes[$theme].basic600T16 }")

      v-tabs-items(v-model="campaignTab" touchless)
        v-tab-item
          segment-patient(:group="id" :segment="segment" @deleted="onDeleteCustomer")
        v-tab-item
          sms-blast-report(:group="id")
        v-tab-item
          email-blast-report(:group="id")

    template(v-else)
      .primary400--text Data not found
    delete-segment-dialog(v-if="showDeleteDialog" :open.sync="showDeleteDialog" :segments="[segment]" @deleted="onDeleted")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import SegmentPatient from '@/components/clinic/segments/SegmentPatient.vue'
import SmsBlastReport from '@/components/clinic/segments/SmsBlastReport.vue'
import EmailBlastReport from '@/components/clinic/segments/EmailBlastReport.vue'
import DeleteSegmentDialog from '@/components/module/segment/DeleteSegmentDialog.vue'

export default {
  props: ['id'],
  components: { SegmentPatient, SmsBlastReport, EmailBlastReport, DeleteSegmentDialog },
  data: () => ({
    campaignTab: 0,
    segment: null,
    isEditingTitle: false,
    isEditingDescription: false,
    isLoading: true,
    isSaving: false,
    isSavingDescription: false,
    editedName: '',
    editedDescription: '',
    showDeleteDialog: false,
    rules: {
      not_empty: [v => v.trim().length || 'This field is required']
    },
    addOptions: [
      {
        id: 'customer',
        title: 'Select from customer list'
      }
    ],
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany'
    })
  },
  created() {
    this.fetchSegment()
  },  
  methods: {
    ...mapActions(['showMessage']),
    fetchSegment() {
      this.isLoading = true
      this.$axios.get(`/connect/${this.company.slug}/groups/${this.id}?expand=customer`)
      .then(resp => {
        this.segment = resp.data
      })
      .catch(() => {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      })
      .finally(() => {
        this.isLoading = false
      })
    },
    editSegmentName() {
      this.isEditingTitle = true
      this.editedName = this.segment.title
    },
    editSegmentDescription() {
      this.isEditingDescription = true
      this.editedDescription = this.segment.description
    },
    saveEdit() {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isSaving = true
      const params = {
        title: this.editedName.trim(),
      }
      this.$axios.patch(`/connect/${this.company.slug}/groups/${this.id}`, params)
      .then(resp => {
        this.isEditingTitle = false
        this.showMessage(['success', this.$t('success_message.segment_title_changed_successfully')])
        this.segment = resp.data
      })
      .catch(() => {
        this.showMessage(['error', this.$t('error_message.unable_to_change_segment_title')])
      })
      .finally(() => {
        this.isSaving = false
      })
    },
    saveEditDescription() {
      this.isSavingDescription = true
      const params = {
        description: this.editedDescription
      }
      this.$axios.patch(`/connect/${this.company.slug}/groups/${this.id}`, params)
      .then(resp => {
        this.isEditingDescription = false
        this.showMessage(['success', this.$t('success_message.segment_description_changed_successfully')])
        this.segment = resp.data
      })
      .catch(() => {
        this.showMessage(['error', this.$t('error_message.unable_to_change_segment_description')])
      })
      .finally(() => {
        this.isSavingDescription = false
      })
    },
    openAddPage(item) {
      switch (item.id) {
        case 'customer':
          this.$router.push(`/admin/clinic/segments/${this.segment.id}/add-customers`)
          break
      }
    },
    onDeleted() {
      this.$router.push('/admin/clinic/segments')
    },
    onDeleteCustomer() {
      this.fetchSegment()
    },
    blastSmsToAll () {
      this.$router.push(`/admin/clinic/segments/${this.id}/send-message`)
    },
    blastEmailToAll () {
      this.$router.push(`/admin/clinic/segments/${this.id}/send-email`)
    }
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}
.main-container {
  position: relative;
}
.separator {
  position: absolute;
  height:2px;
  top: 38px;
  left: 0px;
  right: 0px;
}
</style>